/**
 * Route & amount section for prepared drop modals.
 * Supports edit mode (required) and bulk mode (leave unchanged).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { DollarSign, Filter, Route } from 'lucide-react';

import { Button } from '@/components/ui';
import { formatCost } from '@/lib/utils';
import { DateTimePicker } from '@/components/ui';
import { Input } from '@/components/ui';
import { SearchableSelect } from '@/components/ui';
import { TimeIntervalPicker } from '@/components/ui';
import { LeadsListTags, NO_TAGS_VALUE, TagFilterDropdown } from '@/features/smswiser-campaigns/components/shared';
import {
  scheduleLocalToUtcIso,
  utcIsoToScheduleLocal,
} from '@/features/smswiser-campaigns/utils/scheduleTimezone';
import { useDefaultCampaign } from '@/features/smswiser-campaigns/hooks/useDefaultCampaign';
import { useToast } from '@/providers/ToastProvider';
import type { GatewayOption, TldOption } from '../types';

export interface CampaignOption {
  id: number;
  name: string;
}

interface RouteAmountSectionProps {
  mode: 'edit' | 'bulk';
  gateways: GatewayOption[];
  /** Bulk mode only: campaigns for move-drop (Leave unchanged). Omit or empty to hide. */
  campaigns?: CampaignOption[];
  wiserId?: number | '';
  setWiserId?: (v: number | '') => void;
  tlds: TldOption[];
  routeId: number | '';
  setRouteId: (v: number | '') => void;
  tldValue: string;
  setTldValue: (v: string) => void;
  amount: number | '';
  setAmount: (v: number | '') => void;
  executeAfter: string;
  setExecuteAfter: (v: string) => void;
  /** Bulk mode only: remove schedule from all selected drafts */
  clearSchedule?: boolean;
  onRemoveSchedule?: () => void;
  costInfo: { perSms: number; perDraft?: number; total: number | null } | null;
  count?: number;
  isBusy: boolean;
  /** When false, hide the schedule drop block (e.g. Copy Lists as Draft uses workDate instead). */
  showSchedule?: boolean;
  /** Bulk mode only: interval between scheduled drops (HH:MM). */
  scheduleInterval?: string;
  setScheduleInterval?: (v: string) => void;
  /** Performance tags by gatewayId (Voluum format: gatewayId_routeId). When provided, tags append to gateway labels. */
  tagsByGatewayId?: Record<string, string[]>;
  tagColors?: Record<string, string>;
  /** When set (bulk mode), enables Save as default / Apply default for campaign. */
  countryKey?: string;
  /** Stack Gateway/TLD full-width — for narrow host columns (~300px) where
   *  two selects side by side clip their own labels. */
  stack?: boolean;
}

export const RouteAmountSection: FC<RouteAmountSectionProps> = ({
  mode,
  gateways,
  campaigns = [],
  wiserId,
  setWiserId,
  tlds,
  routeId,
  setRouteId,
  tldValue,
  setTldValue,
  amount,
  setAmount,
  executeAfter,
  setExecuteAfter,
  clearSchedule,
  onRemoveSchedule,
  costInfo,
  count = 1,
  isBusy,
  showSchedule = true,
  scheduleInterval,
  setScheduleInterval,
  tagsByGatewayId,
  tagColors,
  countryKey,
  stack = false,
}) => {
  const [gatewayTagFilter, setGatewayTagFilter] = useState<string>('');
  const { defaultCampaignId, setDefaultCampaign } = useDefaultCampaign(countryKey ?? '');
  const { success: toastSuccess } = useToast();

  const handleSaveAsDefault = () => {
    const wid = wiserId === '' ? 0 : (wiserId as number);
    if (wid <= 0) return;
    setDefaultCampaign(wid);
    const name = campaigns.find((c) => c.id === wid)?.name ?? 'Campaign';
    toastSuccess(`${name} saved as default`);
  };

  const getTagsForRouteId = (rid: number | ''): string[] | undefined => {
    if (rid === '' || !tagsByGatewayId) return undefined;
    const direct = tagsByGatewayId[String(rid)];
    if (direct) return direct;
    const matchingKey = Object.keys(tagsByGatewayId).find(
      (k) => k.endsWith(`_${rid}`) || k === String(rid),
    );
    return matchingKey ? tagsByGatewayId[matchingKey] : undefined;
  };
  const availableGatewayTags = useMemo(() => {
    if (!tagsByGatewayId) return [];
    const set = new Set<string>();
    for (const tags of Object.values(tagsByGatewayId)) {
      for (const t of tags ?? []) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagsByGatewayId]);
  const formatGatewayLabel = (g: GatewayOption): string => {
    const base = `${g.name}${g.pricePerSms != null ? ` ($${Number(g.pricePerSms).toFixed(3)}/SMS)` : ''}`;
    const tags = getTagsForRouteId(g.id);
    if (!tags?.length) return base;
    const tagStr = tags.map((t) => `[${t}]`).join(' ');
    return `${base} ${tagStr}`;
  };
  const gatewayOptions = useMemo(() => {
    const options = gateways.map((g) => {
      const label = formatGatewayLabel(g);
      const tags = getTagsForRouteId(g.id);
      const base = `${g.name}${g.pricePerSms != null ? ` ($${Number(g.pricePerSms).toFixed(3)}/SMS)` : ''}`;
      const optionContent =
        tags?.length ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <span>{base}</span>
            <LeadsListTags performanceTags={tags} tagColors={tagColors} compact />
          </span>
        ) : undefined;
      // Closed trigger is a single h-10 line: the wrapping optionContent grew
      // a second row there and its tag chips crossed the border. One line,
      // name truncates first, chips clip cleanly inside the button.
      const triggerContent =
        tags?.length ? (
          <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden whitespace-nowrap">
            <span className="min-w-0 flex-1 truncate">{base}</span>
            {/* One chip + the +N pill: the NAME identifies the pick — full
                chips live in the dropdown and in the +N hover. */}
            <LeadsListTags performanceTags={tags} tagColors={tagColors} compact maxVisible={1} />
          </span>
        ) : undefined;
      return { value: g.id, label, optionContent, triggerContent };
    });
    if (!gatewayTagFilter || !tagsByGatewayId) return options;
    if (gatewayTagFilter === NO_TAGS_VALUE) {
      return options.filter((o) => !getTagsForRouteId(o.value)?.length);
    }
    const q = gatewayTagFilter.toLowerCase();
    return options.filter((o) =>
      getTagsForRouteId(o.value)?.some((t) => t.toLowerCase() === q),
    );
  }, [gateways, gatewayTagFilter, tagsByGatewayId, tagColors]);
  return (
  <section className="space-y-3">
    <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      <Route className="h-3.5 w-3.5" />
      Route & amount
    </h4>
    {/* grid-cols-1, not a bare grid: an implicit auto track sizes to content,
        so a long gateway label pushed the selects past the column edge. */}
    <div className={stack ? 'grid grid-cols-1 gap-3' : 'grid gap-3 sm:grid-cols-2'}>
      {mode === 'bulk' && campaigns.length > 0 && setWiserId && (
        <div className={stack ? undefined : 'sm:col-span-2'}>
          <div className="flex items-center justify-between gap-1">
            <label className="mb-1 block text-xs text-muted-foreground">Campaign</label>
            {countryKey && (
              <span className="text-[10px]">
                {wiserId !== '' && (wiserId as number) > 0 ? (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={handleSaveAsDefault}
                  >
                    Save as default
                  </button>
                ) : wiserId === '' && defaultCampaignId != null && campaigns.some((c) => c.id === defaultCampaignId) ? (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => setWiserId(defaultCampaignId)}
                  >
                    Apply default
                  </button>
                ) : null}
              </span>
            )}
          </div>
          <SearchableSelect<number>
            options={[
              { value: -1, label: '— Leave unchanged —' },
              ...campaigns.map((c) => ({ value: c.id, label: c.name })),
            ]}
            value={wiserId === '' ? -1 : (wiserId as number)}
            onChange={(v) => setWiserId(v === -1 ? '' : v)}
            placeholder="Select campaign"
            searchPlaceholder="Search campaigns..."
          />
        </div>
      )}
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Gateway</label>
        {mode === 'bulk' ? (
          <SearchableSelect<number>
            options={[
              { value: -1, label: '— Leave unchanged —' },
              ...gatewayOptions,
            ]}
            value={routeId === '' ? -1 : routeId}
            onChange={(v) => setRouteId(v === -1 ? '' : v)}
            placeholder="Select gateway"
            searchPlaceholder="Search gateways..."
            headerSlot={
              availableGatewayTags.length > 0 ? (
                <div className="flex shrink-0 items-center gap-2">
                  <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                  <TagFilterDropdown
                    value={gatewayTagFilter}
                    options={availableGatewayTags}
                    tagColors={tagColors}
                    displayLabel={
                      gatewayTagFilter === ''
                        ? 'All tags'
                        : gatewayTagFilter === NO_TAGS_VALUE
                          ? 'No tags'
                          : gatewayTagFilter
                    }
                    onSelect={setGatewayTagFilter}
                    placeholder="All tags"
                    compact
                  />
                </div>
              ) : undefined
            }
          />
        ) : (
          <SearchableSelect<number>
            options={[
              { value: 0, label: 'Select gateway' },
              ...gatewayOptions,
            ]}
            value={routeId === '' ? 0 : (routeId as number)}
            onChange={(v) => setRouteId(v === 0 ? '' : v)}
            placeholder="Select gateway"
            searchPlaceholder="Search gateways..."
            headerSlot={
              availableGatewayTags.length > 0 ? (
                <div className="flex shrink-0 items-center gap-2">
                  <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                  <TagFilterDropdown
                    value={gatewayTagFilter}
                    options={availableGatewayTags}
                    tagColors={tagColors}
                    displayLabel={
                      gatewayTagFilter === ''
                        ? 'All tags'
                        : gatewayTagFilter === NO_TAGS_VALUE
                          ? 'No tags'
                          : gatewayTagFilter
                    }
                    onSelect={setGatewayTagFilter}
                    placeholder="All tags"
                    compact
                  />
                </div>
              ) : undefined
            }
          />
        )}
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">TLD</label>
        <SearchableSelect<string>
          options={
            mode === 'bulk'
              ? [
                  { value: '__bulk_unchanged__', label: '— Leave unchanged —' },
                  ...tlds.map((t) => ({ value: t.value, label: t.displayName })),
                ]
              : [
                  { value: '', label: 'Select TLD' },
                  ...tlds.map((t) => ({ value: t.value, label: t.displayName })),
                ]
          }
          value={mode === 'bulk' ? (tldValue || '__bulk_unchanged__') : (tldValue || '')}
          onChange={(v) =>
            setTldValue(mode === 'bulk' && v === '__bulk_unchanged__' ? '' : v)
          }
          placeholder="Select TLD"
          searchPlaceholder="Search TLDs..."
        />
      </div>
    </div>
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">
        Amount {mode === 'bulk' && '(leave blank unchanged)'}
      </label>
      <Input
        type="number"
        min={1}
        placeholder={mode === 'bulk' ? 'Leave unchanged' : undefined}
        className="h-10"
        value={amount === '' ? '' : amount}
        error={
          mode === 'edit' && (amount === '' || (typeof amount === 'number' && amount < 1))
            ? 'Min 1 required'
            : undefined
        }
        onChange={(e) =>
          setAmount(e.target.value === '' ? '' : Number(e.target.value))
        }
      />
      {costInfo && (
        <div className="mt-2 flex gap-2 text-xs">
          <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">
            {costInfo.perDraft != null && mode === 'bulk'
              ? `${count} drafts × ${formatCost(costInfo.perDraft)} = `
              : costInfo.total != null
                ? 'Cost:'
                : 'Price per SMS: '}
          </span>
          <span className="font-semibold text-destructive">
            {costInfo.total != null
              ? formatCost(costInfo.total)
              : `$${costInfo.perSms.toFixed(3)}/SMS`}
          </span>
        </div>
      )}
    </div>
    {showSchedule && (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">
        Schedule drop{' '}
        <span className="text-muted-foreground/80">
          (optional{mode === 'bulk' ? ', leave blank unchanged' : ''})
        </span>
      </label>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <DateTimePicker
            value={executeAfter ? utcIsoToScheduleLocal(executeAfter) : ''}
            onChange={(v) => setExecuteAfter(v ? scheduleLocalToUtcIso(v) : '')}
            placeholder="dd/mm/yyyy, --:--"
            disabled={isBusy}
            minDateToday
            ariaLabel="Schedule drop date and time"
            className="min-w-0 flex-1"
          />
        </div>
        {mode === 'bulk' && setScheduleInterval !== undefined && (
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">
              Interval between drafts{' '}
              <span className="text-muted-foreground/80">(hh:mm)</span>
            </label>
            <div className="flex items-center gap-2">
              <TimeIntervalPicker
                value={scheduleInterval ?? '00:00'}
                onChange={setScheduleInterval}
                disabled={isBusy || !executeAfter}
              />
              <span className="text-xs text-muted-foreground">
                per draft
              </span>
            </div>
          </div>
        )}
        {onRemoveSchedule && (mode === 'bulk' || executeAfter) && (
          <Button
            type="button"
            variant={clearSchedule ? 'default' : 'outline'}
            size="sm"
            className="w-fit text-xs"
            onClick={onRemoveSchedule}
            disabled={isBusy}
          >
            {clearSchedule ? 'Remove schedule (applied)' : mode === 'edit' ? 'Remove schedule' : 'Remove schedule from all'}
          </Button>
        )}
      </div>
    </div>
    )}
  </section>
  );
};
